import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { sessionStorageService } from '@/services/sessionStorageService';
import { customerAPI } from '@/services/customerAPI';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, FileText, Search, Clock } from 'lucide-react';
import { logger } from '@/utils/logger';

/**
 * Component that handles persistence of pending batch/search data after login
 * This should be rendered in pages where users might log in
 */
export const PendingDataHandler: React.FC = () => {
  const { user, isAuthenticated, refreshUser } = useCustomerAuth();
  const navigate = useNavigate();
  const [isPersisting, setIsPersisting] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [pendingCount, setPendingCount] = useState({ batches: 0, searches: 0 });

  useEffect(() => {
    // Only check for pending data when user just logged in
    if (!isAuthenticated || !user) {
      return;
    }

    const checkPendingData = async () => {
      try {
        const batches = sessionStorageService.getPendingBatches();
        const searches = sessionStorageService.getPendingSearches();

        if (batches.length > 0 || searches.length > 0) {
          setPendingCount({ batches: batches.length, searches: searches.length });
          setShowPrompt(true);
        }
      } catch (error) {
        logger.error('Failed to check pending data:', error);
      }
    };

    checkPendingData();
  }, [isAuthenticated, user]);

  const handlePersistData = async () => {
    if (!user) return;

    setIsPersisting(true);

    try {
      const batches = sessionStorageService.getPendingBatches();
      const searches = sessionStorageService.getPendingSearches();

      logger.info('Persisting pending data', {
        userId: user.userId,
        batchCount: batches.length,
        searchCount: searches.length
      });

      // Persist batches to user history
      for (const batch of batches) {
        if (batch.jobId) {
          // This is a COMPLETED batch from results page - it's already processed
          // Just need to link it to the user account (backend will handle this)
          logger.info('Completed batch will be linked to user account:', batch.jobId);

          // TODO: When backend implements link_guest_batch operation, call it here:
          // await accountServiceAPI.linkGuestBatches(user.email, user.userId);

          sessionStorageService.clearPendingBatch(batch.sessionId);
        } else if (batch.file && batch.rowCount !== undefined) {
          // This is a PENDING batch - needs to be uploaded and processed
          await customerAPI.saveBatchToHistory(
            user.userId,
            `pending_${batch.sessionId}`,
            batch.file.name,
            batch.processingType,
            batch.rowCount,
            0, // successfulRows - will be updated when processed
            0, // failedRows
            'pending',
            batch.estimatedCost,
            new Date().toISOString()
          );

          sessionStorageService.clearPendingBatch(batch.sessionId);
        } else {
          // Invalid batch data - clear it
          logger.warn('Invalid batch data, clearing:', batch.sessionId);
          sessionStorageService.clearPendingBatch(batch.sessionId);
        }
      }

      // Persist searches to user history
      // Note: Searches are typically completed before login,
      // so we may just save them as pending if they weren't completed
      for (const search of searches) {
        // You might want to trigger the search again or save it as pending
        sessionStorageService.clearPendingSearch(search.sessionId);
      }

      // Clear all pending data
      sessionStorageService.clearPendingData();

      // Refresh user context
      refreshUser();

      logger.info('Successfully persisted pending data');

      // Navigate to account dashboard
      navigate('/account');
    } catch (error) {
      logger.error('Failed to persist pending data:', error);
      // Don't clear data if persistence failed
    } finally {
      setIsPersisting(false);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    // User chose not to save pending data
    sessionStorageService.clearPendingData();
    setShowPrompt(false);
    refreshUser();
  };

  if (!showPrompt) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-md w-full p-4">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-6 h-6 text-slate-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-900 mb-1">
              Save Your Work?
            </h3>
            <p className="text-sm text-slate-600">
              We found {pendingCount.batches + pendingCount.searches} item(s) from before you logged in.
            </p>
          </div>
        </div>

        <div className="space-y-2 mb-6">
          {pendingCount.batches > 0 && (
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <FileText className="w-4 h-4 text-slate-600" />
              <span>{pendingCount.batches} pending batch{pendingCount.batches > 1 ? 'es' : ''}</span>
            </div>
          )}
          {pendingCount.searches > 0 && (
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <Search className="w-4 h-4 text-green-600" />
              <span>{pendingCount.searches} pending search{pendingCount.searches > 1 ? 'es' : ''}</span>
            </div>
          )}
        </div>

        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg mb-6 text-sm text-blue-800">
          <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <p>
            Save these to your account so you can access them later from your dashboard.
          </p>
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleDismiss}
            disabled={isPersisting}
            className="flex-1"
          >
            Discard
          </Button>
          <Button
            onClick={handlePersistData}
            disabled={isPersisting}
            className="flex-1"
          >
            {isPersisting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save to Account'
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
};

